import { customAlphabet } from 'nanoid';
import moment from 'moment-timezone';

// Etapas (colunas) do kanban interno de Implantação / Pós-vendas.
// Fonte: Plano de Implantação/Pós-vendas — 7 colunas, do "Ganho" ao uso consolidado.
export const STAGES = {
  welcome: { key: 'welcome', label: 'Boas-vindas', color: '#1890ff' },
  training: { key: 'training', label: 'Treinamento', color: '#eb2f96' },
  completed: { key: 'completed', label: 'Implantação concluída', color: '#722ed1' },
  monitoring: { key: 'monitoring', label: 'Acompanhamento', color: '#52c41a' },
  canceled: { key: 'canceled', label: 'Cancelado', color: '#f5222d' },
};

// Ordem das colunas no board.
export const STAGE_ORDER = [
  'welcome',
  'training',
  'completed',
  'monitoring',
  'canceled',
];

// Etapa inicial de todo card novo (cliente que acabou de fechar a venda).
export const DEFAULT_STAGE = 'welcome';

// Lista pronta de colunas para o board / selects.
export const COLUMNS = STAGE_ORDER.map((key) => ({ id: key, ...STAGES[key] }));

export function getStageMeta(stageKey) {
  return STAGES[stageKey] || { key: stageKey, label: stageKey || '—', color: '#8c8c8c' };
}

// Etapa final (perdido/cancelado). O arraste para ela é bloqueado no board — só pelo
// card, escolhendo o motivo.
export const CANCELED_STAGE = 'canceled';

// Motivos de perda do cliente (cancelamento). Focados em CAUSA RAIZ analisável
// (evita sintoma vago tipo "foi pro concorrente"). "Outras" sempre por último.
export const CANCEL_REASONS = [
  'Muito caro',
  'Parou de responder',
  'Não tem funcionalidade',
  'Não se adaptou ao sistema',
  'Falta de suporte',
  'Problemas técnicos',
  'Fechou a clínica / parou de atender',
  'Comprou e não ativou',
  'Outras',
];

// --- Board de migração (kanban separado para quem precisa de migração) ---
// O card vive nos dois boards: no de pós-vendas agrupa por `stage`, no de migração
// agrupa por `migrationStage` (campo independente). Ordenação própria: `migrationSortOrder`.
export const MIGRATION_STAGES = {
  awaiting_data: { key: 'awaiting_data', label: 'Aguardando dados' },
  needs_collection: { key: 'needs_collection', label: 'Precisa coletar' },
  awaiting_migration: { key: 'awaiting_migration', label: 'Aguardando migração' },
  done: { key: 'done', label: 'Concluída' },
};

export const MIGRATION_STAGE_ORDER = [
  'awaiting_data',
  'needs_collection',
  'awaiting_migration',
  'done',
];

export const DEFAULT_MIGRATION_STAGE = 'awaiting_data';

export const MIGRATION_COLUMNS = MIGRATION_STAGE_ORDER.map((key) => ({
  id: key,
  ...MIGRATION_STAGES[key],
}));

export function getMigrationStageMeta(key) {
  return MIGRATION_STAGES[key] || { key, label: key || '—' };
}

// Quantos "Sistema Base" foram comprados (soma as quantidades dos produtos).
// Cuidado: casa só "sistema base" para não pegar "I.A Base (...)".
export function baseSystemCount(card) {
  const products = (card && Array.isArray(card.products)) ? card.products : [];
  return products
    .filter((p) => (p.name || '').toLowerCase().includes('sistema base'))
    .reduce((sum, p) => sum + (Number(p.quantity) || 0), 0);
}

// Sugestão de maxCustomHealthProfessionals: nº de "Sistema Base" - 1 (titular já é 1).
export function suggestedMaxProfessionals(card) {
  return Math.max(0, baseSystemCount(card) - 1);
}

// Senha temporária legível (sem caracteres ambíguos) para a conta criada nas boas-vindas.
const tempPasswordAlphabet = customAlphabet('ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789', 8);
export function generateTempPassword() {
  return tempPasswordAlphabet();
}

// Divide um nome completo em primeiro nome + sobrenome (o createProfessional exige ambos).
export function splitName(full) {
  const parts = (full || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

// Vídeos iniciais enviados nas boas-vindas. Preencher com os links reais:
//   { label: 'Primeiros passos', url: 'https://...' }
export const WELCOME_VIDEOS = [];

// Corpo da mensagem de boas-vindas. `bold` formata trechos em negrito conforme o
// canal (WhatsApp usa *asterisco*, e-mail texto puro).
function welcomeBody(card, bold) {
  const loginUrl = `${process.env.REACT_APP_FIREBASE_DOMAIN}/signin`;
  const firstName = (card.contactName || card.clinicName || '').trim().split(/\s+/)[0] || '';
  const greeting = firstName ? `Olá, ${firstName}!` : 'Olá!';
  const login = card.accountEmail || card.email || '';
  const password = card.tempPassword || '';
  const lines = [
    `${greeting} 👋`,
    '',
    `Seja muito bem-vindo(a) ao ${bold('Meagenda')}! 🎉`,
    '',
    'Sua conta já foi criada e está pronta para uso. Abaixo estão os seus dados de acesso:',
    '',
    `🔗 ${bold('Acesse o sistema:')} ${loginUrl}`,
    `👤 ${bold('Login:')} ${login}`,
    `🔑 ${bold('Senha temporária:')} ${password}`,
    '',
    `⚠️ ${bold('Importante:')} Por segurança, recomendamos que você altere sua senha no primeiro acesso.`,
    '',
    'Se precisar de qualquer ajuda ou tiver alguma dúvida durante a configuração ou utilização do sistema, é só responder esta mensagem. Nossa equipe estará à disposição para ajudar.',
    '',
    'Desejamos muito sucesso com o Meagenda! 🚀',
  ];
  return lines.join('\n');
}

// Mensagem de boas-vindas para WhatsApp (negrito com *asterisco*).
export function buildWelcomeMessage(card) {
  return welcomeBody(card, (t) => `*${t}*`);
}

// E-mail de boas-vindas em HTML (inline styles p/ compatibilidade com clientes de e-mail).
export function buildWelcomeEmailHtml(card) {
  const loginUrl = `${process.env.REACT_APP_FIREBASE_DOMAIN}/signin`;
  const firstName = (card.contactName || card.clinicName || '').trim().split(/\s+/)[0] || '';
  const greeting = firstName ? `Olá, ${firstName}!` : 'Olá!';
  const login = card.accountEmail || card.email || '';
  const password = card.tempPassword || '';

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>
<body style="margin:0;padding:0;background-color:#f0f2f5;font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#f0f2f5;padding:24px 0;">
    <tr>
      <td align="center">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background-color:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.06);">
          <tr>
            <td style="background:linear-gradient(135deg,#1890ff 0%,#0050b3 100%);padding:40px 40px 32px;text-align:center;">
              <div style="font-size:26px;font-weight:700;color:#ffffff;letter-spacing:-0.5px;">Meagenda</div>
              <div style="font-size:15px;color:rgba(255,255,255,0.85);margin-top:6px;">Bem-vindo(a) à bordo! 🎉</div>
            </td>
          </tr>
          <tr>
            <td style="padding:36px 40px 8px;">
              <p style="font-size:18px;font-weight:600;color:#1f2329;margin:0 0 16px;">${greeting} 👋</p>
              <p style="font-size:15px;line-height:1.6;color:#4b5563;margin:0 0 24px;">
                Sua conta no <strong>Meagenda</strong> já foi criada e está pronta para uso.
                Abaixo estão os seus dados de acesso:
              </p>
            </td>
          </tr>
          <tr>
            <td style="padding:0 40px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#f7f9fc;border:1px solid #e6ebf2;border-radius:12px;">
                <tr>
                  <td style="padding:20px 24px;">
                    <p style="margin:0 0 4px;font-size:12px;text-transform:uppercase;letter-spacing:0.05em;color:#8a94a6;">Login</p>
                    <p style="margin:0 0 16px;font-size:16px;font-weight:600;color:#1f2329;">${login}</p>
                    <p style="margin:0 0 4px;font-size:12px;text-transform:uppercase;letter-spacing:0.05em;color:#8a94a6;">Senha temporária</p>
                    <p style="margin:0;font-size:18px;font-weight:700;color:#1890ff;font-family:Menlo,Consolas,monospace;letter-spacing:1px;">${password}</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr>
            <td style="padding:28px 40px 8px;text-align:center;">
              <a href="${loginUrl}" target="_blank" style="display:inline-block;background:#1890ff;color:#ffffff;font-size:16px;font-weight:600;text-decoration:none;padding:14px 40px;border-radius:10px;">
                Acessar o sistema
              </a>
            </td>
          </tr>
          <tr>
            <td style="padding:24px 40px 8px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#fff7e6;border:1px solid #ffe7ba;border-radius:10px;">
                <tr>
                  <td style="padding:14px 18px;font-size:13px;line-height:1.5;color:#874d00;">
                    ⚠️ <strong>Importante:</strong> por segurança, recomendamos que você altere a sua senha no primeiro acesso.
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr>
            <td style="padding:24px 40px 36px;">
              <p style="font-size:14px;line-height:1.6;color:#4b5563;margin:0 0 20px;">
                Se precisar de qualquer ajuda ou tiver alguma dúvida durante a configuração ou utilização do sistema,
                é só responder este e-mail. Nossa equipe estará à disposição para ajudar.
              </p>
              <p style="font-size:14px;line-height:1.6;color:#1f2329;margin:0;font-weight:600;">
                Desejamos muito sucesso com o Meagenda! 🚀
              </p>
            </td>
          </tr>
          <tr>
            <td style="background-color:#f7f9fc;padding:20px 40px;text-align:center;border-top:1px solid #e6ebf2;">
              <p style="font-size:12px;color:#8a94a6;margin:0;">Equipe Meagenda · Este é um e-mail automático de boas-vindas.</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

// Boas-vindas para e-mail: assunto + corpo em texto puro (fallback) + HTML.
export function buildWelcomeEmail(card) {
  return {
    subject: 'Bem-vindo(a) ao Meagenda 🎉',
    text: welcomeBody(card, (t) => t),
    html: buildWelcomeEmailHtml(card),
  };
}

// Monta a URL do WhatsApp (wa.me). Número BR sem DDI (<=11 dígitos) recebe 55.
export function buildWhatsAppUrl(phone, text) {
  const digits = (phone || '').replace(/\D/g, '');
  const withCountry = digits && digits.length <= 11 ? `55${digits}` : digits;
  return `https://wa.me/${withCountry}?text=${encodeURIComponent(text)}`;
}

// Mensagem pronta para combinar o treinamento com o cliente (via WhatsApp).
export function buildTrainingScheduleMessage(card) {
  const firstName = (card.contactName || card.clinicName || '').trim().split(/\s+/)[0] || '';
  const greeting = firstName ? `Olá, ${firstName}!` : 'Olá!';
  return [
    `${greeting} 👋`,
    '',
    'Vamos agendar o seu treinamento do Meagenda? 🎉',
    'É rapidinho e nele você aprende a usar o sistema no dia a dia.',
    '',
    'Qual o melhor dia e horário para você nesta semana?',
    '',
    'Fico no aguardo! 🚀',
  ].join('\n');
}

// Vendedores conhecidos (espelha o SELLERS_BY_ID do webhook do Agendor, que já manda
// o `soldBy` com o nome canônico). PREENCHER o WhatsApp/e-mail de cada um para o botão
// "Falar com vendedor" abrir o chat direto.
export const SELLERS = {
  'Miguel Fuentes': { whatsapp: '+5544999994822', email: '' },
  'Michel Bernadino': { whatsapp: '+5544991017851', email: '' },
  'Rafael Mioranza': { whatsapp: '+5545991014011', email: '' },
};

export function sellerInfo(card) {
  return SELLERS[(card && card.soldBy) || ''] || null;
}

// "Falar com vendedor": se o vendedor está mapeado e tem WhatsApp, abre o chat DIRETO
// com mensagem de contexto; senão (número vazio), buildWhatsAppUrl abre o seletor.
export function buildSellerWhatsAppUrl(card) {
  const text = `Olá! Sobre a implantação do cliente *${card.clinicName || ''}*: `;
  const seller = sellerInfo(card);
  return buildWhatsAppUrl(seller ? seller.whatsapp : '', text);
}

// Monta a URL mailto (assunto + corpo) para abrir o cliente de e-mail.
export function buildMailtoUrl(email, subject, body) {
  return `mailto:${email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

// E-mail enviado quando alguém é mencionado em um comentário de um card.
export function buildMentionEmail({
  recipientName, authorName, clinicName, snippet,
}) {
  const firstName = (recipientName || '').trim().split(/\s+/)[0] || '';
  const greeting = firstName ? `Olá, ${firstName}!` : 'Olá!';
  const who = authorName || 'Alguém';
  const where = clinicName ? ` no card de ${clinicName}` : '';
  const subject = `${who} mencionou você em um comentário · Meagenda`;
  const text = [
    greeting,
    '',
    `${who} mencionou você em um comentário${where}:`,
    '',
    `"${snippet || ''}"`,
    '',
    'Acesse o Meagenda para responder.',
  ].join('\n');
  const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>
<body style="margin:0;padding:0;background-color:#f0f2f5;font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#f0f2f5;padding:24px 0;">
    <tr><td align="center">
      <table role="presentation" width="560" cellpadding="0" cellspacing="0" style="max-width:560px;width:100%;background-color:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.06);">
        <tr><td style="background:linear-gradient(135deg,#1890ff 0%,#0050b3 100%);padding:32px 40px;text-align:center;">
          <div style="font-size:22px;font-weight:700;color:#ffffff;">Você foi mencionado(a) 💬</div>
        </td></tr>
        <tr><td style="padding:32px 40px 8px;">
          <p style="font-size:16px;font-weight:600;color:#1f2329;margin:0 0 14px;">${greeting}</p>
          <p style="font-size:15px;line-height:1.6;color:#4b5563;margin:0 0 20px;">
            <strong>${who}</strong> mencionou você em um comentário${where}:
          </p>
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#f7f9fc;border:1px solid #e6ebf2;border-radius:12px;">
            <tr><td style="padding:16px 20px;font-size:14px;line-height:1.6;color:#262626;white-space:pre-wrap;">${(snippet || '').replace(/</g, '&lt;')}</td></tr>
          </table>
        </td></tr>
        <tr><td style="padding:24px 40px 36px;">
          <p style="font-size:14px;line-height:1.6;color:#4b5563;margin:0;">Acesse o Meagenda para ver o card e responder.</p>
        </td></tr>
        <tr><td style="background-color:#f7f9fc;padding:20px 40px;text-align:center;border-top:1px solid #e6ebf2;">
          <p style="font-size:12px;color:#8a94a6;margin:0;">Equipe Meagenda · notificação automática de menção.</p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body></html>`;
  return { subject, text, html };
}

// Avatar estilo Gmail: cor + iniciais do cliente.
export const AVATAR_COLORS = [
  '#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5',
  '#2196f3', '#039be5', '#0097a7', '#009688', '#43a047',
  '#689f38', '#ef6c00', '#f4511e', '#6d4c41', '#546e7a',
];

export function randomAvatarColor() {
  return AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)];
}

// Cor determinística a partir de um nome (avatar do vendedor, estilo Gmail).
export function colorForName(name) {
  const s = name || '';
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash * 31 + s.charCodeAt(i)) % 2147483647;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

// Cor do avatar: a salva no card, ou um fallback determinístico pelo nome (cards antigos).
export function avatarColorFor(card) {
  if (card && card.avatarColor) return card.avatarColor;
  const name = (card && (card.clinicName || card.contactName)) || '';
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) % 2147483647;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

// O cliente precisa de migração? (produto "Migração" no Agendor marca migration:true;
// manualmente, basta ter o nome do sistema anterior ou o link do Drive.)
export function needsMigration(card) {
  if (!card) return false;
  if (card.migration === 'Não' || card.migration === false) return false;
  return card.migration === true
    || card.migration === 'Sim'
    || !!card.migrationSystem
    || !!card.migrationDriveLink;
}

// Treinamento pendente = tem data agendada e ainda não foi marcado como concluído.
// Usado na faixa de atenção acima do board (treinamento não é uma coluna — pode
// acontecer com ou sem migração, em qualquer momento).
export function trainingPending(card) {
  return !!card.trainingDate && !card.trainingDone;
}

// Data/hora do treinamento formatada para exibição.
export function formatTrainingDate(value) {
  if (!value) return null;
  const m = moment(value);
  return m.isValid() ? m.format('DD/MM/YYYY [às] HH:mm') : null;
}

// Contagem regressiva até o treinamento. Retorna { past, text } ou null.
export function trainingCountdown(value) {
  if (!value) return null;
  const target = moment(value);
  if (!target.isValid()) return null;
  const diffMs = target.diff(moment());
  if (diffMs <= 0) return { past: true, text: 'Treinamento já realizado' };
  const dur = moment.duration(diffMs);
  const days = Math.floor(dur.asDays());
  const hours = dur.hours();
  const minutes = dur.minutes();
  let text;
  if (days > 0) {
    const base = days === 1 ? 'Falta 1 dia' : `Faltam ${days} dias`;
    text = hours > 0 ? `${base} e ${hours}h` : base;
  } else if (hours > 0) {
    text = minutes > 0 ? `Faltam ${hours}h ${minutes}min` : `Faltam ${hours}h`;
  } else {
    text = minutes <= 1 ? 'Falta menos de 1 min' : `Faltam ${minutes} min`;
  }
  return { past: false, text };
}

export function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

// Catálogo padrão de produtos/serviços (valor unitário sugerido).
export const PRODUCT_CATALOG = [
  { name: 'Migração', unitValue: 600 },
  { name: 'Confirmação de consulta', unitValue: 99 },
  { name: 'I.A (Agendamento)', unitValue: 200 },
  { name: 'I.A Base (Numero de ativação)', unitValue: 699 },
  { name: 'I.A De prontuario', unitValue: 149 },
  { name: 'Secretaria Remota', unitValue: 1499 },
  { name: 'Sistema Base', unitValue: 99 },
  { name: 'TV - Profissional Principal', unitValue: 149 },
  { name: 'TV - Profissionais Adicionais', unitValue: 29 },
];

// Link direto de conversa no WhatsApp (sem mensagem). Retorna null se não houver número.
export function whatsAppLink(phone) {
  const digits = (phone || '').replace(/\D/g, '');
  if (!digits) return null;
  const withCountry = digits.length <= 11 ? `55${digits}` : digits;
  return `https://wa.me/${withCountry}`;
}
